/**
 * PartnerPayoutWeek — admin mirror of fleet-partner's PayoutWeekDetail.tsx.
 *
 * Uses `get_partner_week_contracts` RPC for server-side pagination + search,
 * replaces the old client-side computation approach.
 *
 * Table columns match fleet-partner: Vehicle | Payout | Paid On | Status
 * Includes vehicle photos with hover preview (admin feature).
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { ListPagination } from '@/components/ListPagination';
import { WeekPicker } from '@/components/WeekPicker';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { ArrowLeft, HandCoins, Search, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { businessWeekBounds, formatBusinessDate, formatCurrency } from '@/lib/format';
import { fetchPayoutWeekContracts, payoutWeekStats, type WeekContract } from '@/components/partners/payoutWeekTotals';

function getSbtPhotoUrl(listingUrl: string | null, updatedAt?: string | null): string | null {
    if (!listingUrl) return null;
    const match = listingUrl.match(/sbtjapan\.com\/used-cars\/([A-Z0-9]+)/i);
    if (!match) return null;
    const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : '';
    return `https://img.sbtjapan.com/img/carphoto/${match[1]}/normal/f.jpg${cacheBust}`;
}

const PAGE_SIZE = 15;

// ─── Component ───────────────────────────────────────────────────────────

export default function PartnerPayoutWeek() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Read params
    const weekParam = searchParams.get('week');
    const partnerIdParam = searchParams.get('partner');
    const partnerNameParam = searchParams.get('name');
    const partnerId = partnerIdParam ? parseInt(partnerIdParam) : null;

    // Derive the selected week range on the Ghana calendar — `?week=` is a calendar day, and the
    // bounds it produces have to match the day the repayments were attributed to.
    const week = useMemo(() => businessWeekBounds(weekParam), [weekParam]);
    const { weekStart, weekEnd } = week;

    const weekDate = useMemo(() => ({
        from: weekStart,
        to: weekEnd,
    }), [weekStart, weekEnd]);

    // ─── Data Fetching ───────────────────────────────────────────────

    const { data: response, isLoading } = useQuery({
        queryKey: ['partner-week-contracts-admin', partnerId, week.fromInstant, week.toInstant, currentPage, debouncedSearch],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_week_contracts', {
                p_partner_id: partnerId!,
                p_from_date: week.fromInstant,
                p_to_date: week.toInstant,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                ...(debouncedSearch.trim() ? { p_search: debouncedSearch.trim() } : {}),
            });
            if (error) throw error;
            const rows = (data as WeekContract[]) || [];
            return {
                rows,
                totalItems: rows.length > 0 ? rows[0].total_contracts : 0,
            };
        },
        enabled: !!partnerId,
    });

    const contracts = response?.rows ?? [];
    const totalItems = response?.totalItems ?? 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    // ─── Stats ───────────────────────────────────────────────────────

    // The cards describe the whole week: every contract in it, whatever page or search the table is
    // on. They used to add up the table's 15-row page (see payoutWeekTotals.ts).
    const { data: weekContracts, isLoading: isLoadingWeek, error: weekError } = useQuery({
        queryKey: ['partner-week-contracts-admin-totals', partnerId, week.fromInstant, week.toInstant],
        queryFn: () => fetchPayoutWeekContracts(partnerId!, week.fromInstant, week.toInstant),
        enabled: !!partnerId,
    });

    const stats = useMemo(() => payoutWeekStats(weekContracts ?? []), [weekContracts]);

    // ─── Handlers ────────────────────────────────────────────────────

    const handleWeekChange = (newWeek: { from: Date; to: Date }) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('week', businessWeekBounds(newWeek.from).weekStartYmd);
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleBack = () => {
        // Navigate back to the partner detail payouts tab
        if (partnerId) {
            navigate(`/dashboard/partners/${partnerId}`);
        } else {
            navigate('/dashboard/partners/assignments');
        }
    };

    // ─── Status Badge ────────────────────────────────────────────────

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'paid': return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
            case 'partial': return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>;
            case 'due': return <Badge className="bg-red-100 text-red-700 border-red-200">Due</Badge>;
            case 'upcoming': return <Badge variant="outline" className="text-muted-foreground">Upcoming</Badge>;
            default: return <Badge variant="outline" className="text-muted-foreground">—</Badge>;
        }
    };

    // ─── Render ──────────────────────────────────────────────────────

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <HandCoins className="h-8 w-8" />
                        <h1 className="text-4xl font-semibold tracking-tight">Weekly Payments</h1>
                    </div>
                    <div className="flex items-center gap-2 ml-11">
                        <p className="text-muted-foreground">
                            {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
                        </p>
                        {partnerNameParam && (
                            <Badge variant="secondary" className="text-sm">
                                {partnerNameParam}
                            </Badge>
                        )}
                    </div>
                </div>
                <WeekPicker
                    date={weekDate}
                    onDateChange={handleWeekChange}
                />
            </div>

            {/* Content */}
            {isLoading || isLoadingWeek ? (
                <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-5">
                        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-64" />
                </div>
            ) : (
                <>
                    {/* Stat Cards */}
                    {weekError ? (
                        <p className="text-center text-destructive py-8">{(weekError as Error).message}</p>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-5">
                            <StatCard
                                title="Total Amount"
                                value={<CurrencyDisplay amount={stats.totalAmount} />}
                                description="Total partner payout amount for this week."
                            />
                            <StatCard
                                title="Vehicles"
                                value={<span className="text-3xl font-bold">{stats.vehicleCount}</span>}
                                description="Number of unique vehicles with payments this week."
                            />
                            <StatCard
                                title="Avg per Vehicle"
                                value={<CurrencyDisplay amount={stats.avgPerVehicle} />}
                                description="Average partner payout per vehicle for this week."
                            />
                            <StatCard
                                title="Top Earner"
                                value={
                                    <span className="text-lg font-bold truncate">
                                        {stats.topEarner?.vehicle_reg ?? '—'}
                                    </span>
                                }
                                description="Vehicle with the highest payment amount this week."
                                subtitle={stats.topEarner ? formatCurrency(stats.topEarner.partner_portion) : undefined}
                            />
                            <StatCard
                                title="Payout Status"
                                value={
                                    (weekContracts?.length ?? 0) > 0
                                        ? getStatusBadge(stats.overallStatus)
                                        : <Badge variant="outline" className="text-muted-foreground">No payments</Badge>
                                }
                                description="Current payout status for this week."
                            />
                        </div>
                    )}

                    {/* Search + Pagination */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by vehicle..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                            <ListPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                    </div>

                    {/* Table */}
                    {contracts.length > 0 ? (
                        <Card className="border-0">
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Vehicle</TableHead>
                                            <TableHead>Payout</TableHead>
                                            <TableHead>Paid On</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {contracts.map((c) => {
                                            const photoUrl = getSbtPhotoUrl(c.vehicle_listing_url, c.vehicle_updated_at);
                                            return (
                                                <TableRow key={c.contract_id}>
                                                    <TableCell className="font-medium">
                                                        <div className="flex items-center gap-2">
                                                            {photoUrl ? (
                                                                <HoverCard openDelay={200} closeDelay={100}>
                                                                    <HoverCardTrigger asChild>
                                                                        <img
                                                                            src={photoUrl}
                                                                            alt=""
                                                                            className="h-8 w-8 rounded-md border border-gray-200 object-cover shrink-0 cursor-zoom-in"
                                                                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                                                                        />
                                                                    </HoverCardTrigger>
                                                                    <HoverCardContent side="right" align="center" className="w-auto p-1" sideOffset={8}>
                                                                        <img src={photoUrl} alt={c.vehicle_reg || ''} className="w-[480px] rounded-md" />
                                                                    </HoverCardContent>
                                                                </HoverCard>
                                                            ) : null}
                                                            <div
                                                                className={`h-8 w-8 rounded-md border border-gray-200 shrink-0${photoUrl ? ' hidden' : ''}`}
                                                                style={{ backgroundColor: c.vehicle_color || '#e5e7eb' }}
                                                            />
                                                            <div>
                                                                <span className="font-medium">{c.vehicle_reg || `#${c.contract_id}`}</span>
                                                                {(c.vehicle_make || c.vehicle_model) && (
                                                                    <p className="text-xs text-muted-foreground">{[c.vehicle_make, c.vehicle_model].filter(Boolean).join(' ')}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="font-medium">{formatCurrency(c.partner_portion)}</TableCell>
                                                    <TableCell className="text-muted-foreground">
                                                        {c.payout_date ? formatBusinessDate(c.payout_date) : <span>—</span>}
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(c.status)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="py-16 text-center text-muted-foreground flex flex-col items-center gap-2">
                            <Calendar className="h-8 w-8 text-muted-foreground/40" />
                            <p>No payments found for this week.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
